/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import { useEffect } from 'react'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { useAppDispatch } from '../../../../app/hooks'
import { useDashboardData, useDashboardWidgetSetting } from '../../../../hooks/useDashboardState'
import { getWidgetForDashboard } from '../../../../features/widget/asyncThunks'
import { createInsightCount } from '../../../../utils/runSearchUtil'
import { getInsightCategory } from '../../../../utils/viewOptionUtil'
import { deriveCountType, toFilter } from '../../../../utils/widgetUtil'
import InsightCountOptionsVo from '../../../../vo/action/InsightCountOptions'
import Measure from '../../../../enums/Measure'
import CountType from '../../../../enums/CountType'
import { AudienceSentimentWidget } from '../../../../schemas/AudienceSentimentWidgetSchema'
import { Dashboard } from '../../../../schemas/DashboardSchema'
import { RunSearch } from '../../../../schemas/action/RunSearchSchema'

const createSearch = (
  widgetSetting: AudienceSentimentWidget,
  dashboardData: Dashboard
): RunSearch => {
  const filter = toFilter(widgetSetting)
  // FIXME: Not sure the impact, but some extra properties `widgetSetting.excludedTerms` that weren't exsit originally
  // originally this field is lost during `nb.dto.clone(dashboard)` process in `onSuccess_getDashboard_` in show.js

  const measure = Measure.POSTS

  const countType = deriveCountType(widgetSetting)
  const insightCountOptions = InsightCountOptionsVo()
  insightCountOptions.isAuthorBased = countType === CountType.AUTHORS

  const { precision, selectedTopic, viewOption } = widgetSetting
  const { id: dashboardId, name } = dashboardData
  return createInsightCount(
    dashboardId!,
    name!,
    precision!,
    [selectedTopic.topicId!],
    filter,
    measure,
    getInsightCategory(viewOption!),
    insightCountOptions
  )
}

interface RunSearchEffectProps {
  widgetId: number
  children: React.ReactElement
}

const RunSearchEffect = ({ widgetId, children }: RunSearchEffectProps): React.ReactElement => {
  const widgetSetting = useDashboardWidgetSetting<AudienceSentimentWidget>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const search = createSearch(widgetSetting, dashboardData!)

    dispatch(
      getWidgetForDashboard({
        searches: [search],
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, refreshTimestamp])

  return children
}

export default RunSearchEffect
